import { createTheme } from '@mui/material/styles';

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#009E60',
      light: '#00FF00',
      dark: '#006B42',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1a1a1a',
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    info: {
      main: '#1a1a1a',
      light: '#333333',
      dark: '#000000',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    ...Array(19).fill('0 25px 50px -12px rgba(0, 0, 0, 0.25)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 10, padding: '10px 20px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          '&:hover': { boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12 },
        elevation1: { boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 500 },
        standardSuccess: {
          backgroundColor: '#f0fff0',
          color: '#009E60',
          '& .MuiAlert-icon': { color: '#009E60' },
          border: '1px solid #c0e8c0',
        },
        standardError: {
          backgroundColor: '#fff5f5',
          color: '#c41e3a',
          '& .MuiAlert-icon': { color: '#c41e3a' },
          border: '1px solid #f5d5d5',
        },
        standardWarning: {
          backgroundColor: '#fff8e1',
          color: '#f59e0b',
          '& .MuiAlert-icon': { color: '#f59e0b' },
          border: '1px solid #ffe082',
        },
        standardInfo: {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          '& .MuiAlert-icon': { color: '#1976d2' },
          border: '1px solid #90caf9',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
          backgroundColor: '#009E60',
          color: '#ffffff',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00FF00',
      light: '#33FF33',
      dark: '#009E60',
      contrastText: '#000000',
    },
    secondary: {
      main: '#f5f5f5',
      light: '#ffffff',
      dark: '#cccccc',
      contrastText: '#000000',
    },
    success: {
      main: '#4ade80',
      light: '#86efac',
      dark: '#22c55e',
      contrastText: '#000000',
    },
    error: {
      main: '#f87171',
      light: '#fca5a5',
      dark: '#ef4444',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#fbbf24',
      light: '#fcd34d',
      dark: '#f59e0b',
      contrastText: '#000000',
    },
    info: {
      main: '#f5f5f5',
      light: '#ffffff',
      dark: '#cccccc',
    },
    background: {
      default: '#000000',
      paper: '#0f0f0f',
    },
    text: {
      primary: '#ffffff',
      secondary: '#999999',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
    ...Array(19).fill('0 25px 50px -12px rgba(0, 0, 0, 0.5)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 10, padding: '10px 20px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
          '&:hover': { boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)' },
        },
        containedPrimary: {
          backgroundColor: '#00FF00', color: '#000000',
          '&:hover': { backgroundColor: '#33FF33' },
          '&:disabled': { backgroundColor: 'rgba(0, 255, 0, 0.5)', color: 'rgba(0, 0, 0, 0.5)' },
        },
        containedSuccess: {
          backgroundColor: '#4ade80', color: '#000000',
          '&:hover': { backgroundColor: '#86efac' },
          '&:disabled': { backgroundColor: 'rgba(74, 222, 128, 0.5)', color: 'rgba(0, 0, 0, 0.5)' },
        },
        containedError: {
          backgroundColor: '#f87171', color: '#ffffff',
          '&:hover': { backgroundColor: '#fca5a5' },
          '&:disabled': { backgroundColor: 'rgba(248, 113, 113, 0.5)', color: 'rgba(255, 255, 255, 0.5)' },
        },
        containedWarning: {
          backgroundColor: '#fbbf24', color: '#000000',
          '&:hover': { backgroundColor: '#fcd34d' },
          '&:disabled': { backgroundColor: 'rgba(251, 191, 36, 0.5)', color: 'rgba(0, 0, 0, 0.5)' },
        },
        outlinedPrimary: {
          borderColor: '#00FF00', color: '#00FF00',
          '&:hover': { backgroundColor: 'rgba(0, 255, 0, 0.08)', borderColor: '#33FF33', color: '#33FF33' },
        },
        outlinedSuccess: {
          borderColor: '#4ade80', color: '#4ade80',
          '&:hover': { backgroundColor: 'rgba(74, 222, 128, 0.08)', borderColor: '#86efac', color: '#86efac' },
        },
        outlinedError: {
          borderColor: '#f87171', color: '#f87171',
          '&:hover': { backgroundColor: 'rgba(248, 113, 113, 0.08)', borderColor: '#fca5a5', color: '#fca5a5' },
        },
        outlinedWarning: {
          borderColor: '#fbbf24', color: '#fbbf24',
          '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.08)', borderColor: '#fcd34d', color: '#fcd34d' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiIconButton-colorPrimary': { color: '#00FF00', '&:hover': { backgroundColor: 'rgba(0, 255, 0, 0.08)', color: '#33FF33' } },
          '&.MuiIconButton-colorSuccess': { color: '#4ade80', '&:hover': { backgroundColor: 'rgba(74, 222, 128, 0.08)', color: '#86efac' } },
          '&.MuiIconButton-colorError': { color: '#f87171', '&:hover': { backgroundColor: 'rgba(248, 113, 113, 0.08)', color: '#fca5a5' } },
          '&.MuiIconButton-colorWarning': { color: '#fbbf24', '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.08)', color: '#fcd34d' } },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)', backgroundImage: 'none', backgroundColor: '#0f0f0f' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, backgroundImage: 'none' },
        elevation1: { boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#0a0a0a', backgroundImage: 'none', borderRight: '1px solid rgba(255, 255, 255, 0.08)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: '#0f0f0f' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#00FF00' },
          },
          '& .MuiOutlinedInput-input': {
            color: '#ffffff',
            '&::placeholder': { color: 'rgba(255, 255, 255, 0.5)', opacity: 1 },
          },
          '& .MuiInputBase-input': {
            color: '#ffffff',
            '&[type="date"]::-webkit-calendar-picker-indicator': { filter: 'invert(1)', cursor: 'pointer', opacity: 0.7, '&:hover': { opacity: 1 } },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 500 },
        standardSuccess: {
          backgroundColor: '#1a2a1a',
          color: '#00FF00',
          '& .MuiAlert-icon': { color: '#00FF00' },
          border: '1px solid #009E60',
        },
        standardError: {
          backgroundColor: '#2a1a1a',
          color: '#f87171',
          '& .MuiAlert-icon': { color: '#f87171' },
          border: '1px solid #8b1428',
        },
        standardWarning: {
          backgroundColor: '#2a2415',
          color: '#fbbf24',
          '& .MuiAlert-icon': { color: '#fbbf24' },
          border: '1px solid #d97706',
        },
        standardInfo: {
          backgroundColor: '#1a2a3a',
          color: '#90caf9',
          '& .MuiAlert-icon': { color: '#90caf9' },
          border: '1px solid #1976d2',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
          backgroundColor: '#00FF00',
          color: '#000000',
        },
      },
    },
  },
});
